import {
  FormControlLabel,
  Radio,
  RadioGroup,
  type RadioGroupProps,
} from "@mui/material";
import { useTranslations } from "use-intl";
import { getAgeFromDateOfBirth } from "@/app/lib/utils/getAgefromDateOfBirth";
import { getTravellerAgeAtTravelDate } from "@/app/lib/utils/ageValidation";
import { getTravelerTypeOfAge } from "@/app/lib/utils/transformTraveler";
import type { Traveller } from "@/app/lib/types/types";

type Props = RadioGroupProps & {
  travelDate: Date;
  selectedTravellers: Traveller[];
};

export const MainTravellerRadioButtons = ({
  travelDate,
  selectedTravellers,
  ...props
}: Props) => {
  const t = useTranslations();

  return (
    <RadioGroup {...props}>
      {selectedTravellers.map((traveller) => {
        const ageAtTravelDate = getTravellerAgeAtTravelDate(
          traveller.birthDate,
          travelDate,
        );
        const ageGroupAtTravelDate = getTravelerTypeOfAge(ageAtTravelDate);

        const currentAge = getAgeFromDateOfBirth(traveller.birthDate);
        const currentAgeGroup = getTravelerTypeOfAge(currentAge);

        return (
          <FormControlLabel
            key={traveller.id}
            value={traveller.id}
            control={<Radio />}
            disabled={ageGroupAtTravelDate !== "adult"}
            label={
              <div className="flex items-center gap-2">
                <div className="grow" data-sentry-mask>
                  <div className="font-medium text-text-title">
                    {traveller.firstName} {traveller.lastName}
                  </div>
                  <div className="text-xs text-text-tertiary">
                    {t("hotels.overview.age")} {currentAge}
                  </div>
                  {ageGroupAtTravelDate !== currentAgeGroup ? (
                    <div className="mt-1 text-xs text-semantic-warning-strong">
                      {t("travellers.ageValidationError", {
                        ageGroupAtTravelDate,
                        age: ageAtTravelDate,
                      })}
                    </div>
                  ) : null}
                </div>
                {props.value === traveller.id ? (
                  <div className="shrink-0 text-xs font-medium">
                    {t("travellers.mainTraveller")}
                  </div>
                ) : null}
              </div>
            }
            sx={{
              display: "flex",
              gap: "calc(var(--spacing)*4)",
              m: 0,
              p: 2,
              "&:not(:last-of-type)": {
                borderBottom: "1px solid #E5E5E5",
              },
              ".MuiFormControlLabel-label": {
                width: "100%",
              },
            }}
          />
        );
      })}
    </RadioGroup>
  );
};
